import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { setMobileMenuOpen } from "@redux/slices/sideMenuSlice";
import { useWindowSize } from "@shared/hooks/useWindowSize";

const MOBILE_MAX_WIDTH = 1024;

/**
 * Mobile breakpoint, drawer open state, and effects: close on outside click and on route change.
 */
export const useSideMenuMobile = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();
  const isMobileMenuOpen = useAppSelector(
    (state: RootState) => state.sideMenu.isMobileMenuOpen
  );

  const [isMobile, setIsMobile] = useState(() => width <= MOBILE_MAX_WIDTH);

  useEffect(() => {
    const next = width <= MOBILE_MAX_WIDTH;
    setIsMobile((prev) => (prev !== next ? next : prev));
  }, [width]);

  useEffect(() => {
    if (!isMobileMenuOpen || !isMobile) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".mobile-menu-drawer") &&
        !target.closest('[aria-label="Toggle menu"]')
      ) {
        dispatch(setMobileMenuOpen(false));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen, isMobile, dispatch]);

  useEffect(() => {
    if (isMobile) {
      dispatch(setMobileMenuOpen(false));
    }
  }, [pathname, isMobile, dispatch]);

  const closeMobileMenu = () => {
    dispatch(setMobileMenuOpen(false));
  };

  return {
    isMobile,
    isMobileMenuOpen,
    closeMobileMenu,
  };
};
